/* eslint-disable @typescript-eslint/naming-convention */
import { useEffect } from 'react'
import { Outlet, useOutletContext, useParams, useSearchParams } from 'react-router-dom'
import { useComputed, useSignal, useSignalEffect } from '@preact/signals-react'

import BusinessEntityTreeRoot from '../../components/BusinessEntityTreeRoot.jsx'
import Header from '../../components/Header.jsx'
import InactivityModal from '../../components/InactivityModal.jsx'
import LoadingAnimation from '../../components/LoadingAnimation.jsx'
import NavigatorComponent from '../../components/NavigatorComponent.jsx'
import Notification from '../../components/Notification.jsx'

import { fetchBusinessEntity } from '../../state/actions/businessEntityActions.js'
import { fetchUserManifestByAccessToken } from '../../state/actions/userManifestActions.js'
import { updateDebug, updateSelectedBusinessEntityId } from '../../state/actions/userSettingsActions.js'

import { appConfigSignal } from '../../state/signals/appConfigSignals.js'
import { currentBusinessEntitySignal, rootBusinessEntitySignal } from '../../state/signals/businessEntitySignals.js'
import { userPermissionsSignal, userPermissionsSummarySignal } from '../../state/signals/permissionSignals.js'
import { currentUserSignal } from '../../state/signals/userSignals.js'
import { debugSignal, showSidebarSignal, sidebarWidthSignal } from '../../state/signals/userSettingsSignals.js'

import { getPixelMeasurement } from '../../utils/utils.js'

export default function BusinessEntity() {

    const { beId } = useParams()
    const [searchParams] = useSearchParams()

    const { user, config, accessToken, permissionMasks, permissions } = useOutletContext()

    const currentUrlFragmentSignal = useSignal('')

    const initialMouseXSignal = useSignal(null)
    const initialSidebarWidthSignal = useSignal(null)
    const savedSidebarWidthSignal = useSignal(sidebarWidthSignal.value)

    const uniquePermissionMasksBusinessEntityIds = useComputed(() => {
        const roleBusinessEntityIds = permissionMasks.map(pm => pm.roleBusinessEntityIds)
        const flattened = roleBusinessEntityIds.flat()
        const uniqueSet = new Set(flattened)
        const unique = [...uniqueSet]
        return unique
    })

    useEffect(() => {
        // this will store in local storage in case the user goes back to legacy site
        updateSelectedBusinessEntityId(beId)
        fetchUserManifestByAccessToken(config, accessToken)
    }, [beId, config, accessToken])

    useEffect(() => {
        const hasDebugParam = !!searchParams.get('debug')
        if (hasDebugParam) {
            updateDebug(hasDebugParam)
        }
    }, [searchParams])

    useEffect(
        () => {
            if (user && user.businessEntityId) {
                fetchBusinessEntity(
                    user.businessEntityId,
                    config,
                    accessToken,
                    {
                        currentBusinessEntityId: beId,
                        includeChildren: true,
                        includeParent: true,
                        setType: true,
                        excludeMerchants: true,
                        recomputePermissions: true
                    }
                )
            }
        },
        [
            user,
            user.businessEntityId,
            config,
            accessToken,
            beId
        ]
    )

    useSignalEffect(() => {
        const currentPathName = location.pathname
        const match = /\/be\/(\d+)(\/(\w+))+/.test(currentPathName);
        const splat = currentPathName.split('/')
        currentUrlFragmentSignal.value = match ? splat.slice(3, splat.length).join("/") : ''

        // NOTE: run this effect for every re-render. When [location] was provided it did not detect changes to business entity selection
    })

    const accordionData = [
        { name: 'currentBusinessEntity', data: currentBusinessEntitySignal.value},
        { name: 'currentBusinessEntityId', data: beId },
        { name: 'currentUrlFragment', data: currentUrlFragmentSignal.value },
        { name: 'showSidebarSignal', data: showSidebarSignal.value },
        { name: 'Permissions', data: permissions },
        { name: 'PermisionMasks', data: permissionMasks },
        { name: 'User', data: user },
        { name: 'User Permissions', data: userPermissionsSignal.value },
        { name: 'User Permissions Summary', data: userPermissionsSummarySignal.value },
        { name: 'uniquePermissionMasksBusinessEntityIds', data: uniquePermissionMasksBusinessEntityIds}
    ]

    const handleSidebarMouseDown = (event) => {
        event.preventDefault()
        const f = "handleSidebarMouseDown"
        let print = false
        initialMouseXSignal.value = event.clientX
        initialSidebarWidthSignal.value = document.getElementById("sidebar").offsetWidth
        if (print) {
            console.log(`${f}: mouse down detected with client X ${initialMouseXSignal.value} and sidebar width ${initialSidebarWidthSignal.value}`)
        }
        const sidebarHandle = document.getElementById("sidebar-resize-handle")
        sidebarHandle.style.opacity = 1
        window.addEventListener("mousemove", handleSidebarMouseMove)
        window.addEventListener("mouseup", handleSidebarMouseUp)
    }

    const handleSidebarMouseMove = (event) => {
        const f = "handleSidebarMouseMove()"
        let print = false
        const sidebar = document.getElementById("sidebar")
        const currentMouseX = event.clientX
        const dx = currentMouseX - initialMouseXSignal.value
        let newSidebarWidth = initialSidebarWidthSignal.value + dx
        const minWidth = 200 //getPixelMeasurement(sidebar.style['min-width'], sidebar) //temporary fix until logic implemented to change mind width when toggling open/close
        if (newSidebarWidth < minWidth) {
            newSidebarWidth = minWidth
        }
        const cssMaxWidthProperty = sidebar.style['max-width'] //must use this notation
        const maxWidth = getPixelMeasurement(sidebar.style['max-width'], sidebar) // assumes the sidebar's max width is specified and in pixels
        if (print) {
            console.log(`${f}: sidebar specifies its max width as ${cssMaxWidthProperty}, which translates to ${maxWidth} pixels`)
            console.log(sidebar.style)
        }
        if (newSidebarWidth > maxWidth) {
            if (print) {
                console.log(`${f}: calculated new sidebar width exceeds maximum of ${maxWidth}`)
            }
            newSidebarWidth = maxWidth
        }
        if (print) {
            console.log(`${f}: detected mouse movement with initial mouse X ${initialMouseXSignal.value}, current mouse X ${currentMouseX} -> change ${dx} -> new sidebar width ${newSidebarWidth}`)
        }
        sidebarWidthSignal.value = newSidebarWidth
        savedSidebarWidthSignal.value = newSidebarWidth
    }

    const handleSidebarMouseUp = () => {
        const f = "handleSidebarMouseUp()"
        let print = false
        if (print) {
            console.log("Mouse up detected, removing event listeners")
        }
        const sidebarHandle = document.getElementById("sidebar-resize-handle")
        sidebarHandle.style.opacity = null
        window.removeEventListener("mousemove", handleSidebarMouseMove)
        window.removeEventListener("mouseup", handleSidebarMouseUp)
    }

    const minWidthComputed = useComputed(
        () => {
            if (showSidebarSignal.value) {
                return "200px"
            }
            return "50px"
        }
    )

    const handleToggleTree = () => {
        const sidebar = document.getElementById("sidebar")
        showSidebarSignal.value = !showSidebarSignal.value
        if (showSidebarSignal.value) {
            console.log(`Restoring hard coded sidebar width (${savedSidebarWidthSignal.value})`)
            sidebarWidthSignal.value = savedSidebarWidthSignal.value
        } else {
            console.log("Deleting hard coded sidebar width")
            sidebarWidthSignal.value = '50px'
        }
    }

    if (!appConfigSignal.value || !userPermissionsSummarySignal.value || !currentUserSignal.value) {
        return <LoadingAnimation />
    }

    return (
        <>
            <NavigatorComponent />
            <div className="vh-100">
                <Header config={config} />
                <div style={{ height: "calc(100vh - 58px)", display:"flex", position:"relative" }}>
                    <aside
                        className="sidebar bg-light"
                        id="sidebar"
                        data-show-tree={showSidebarSignal.value ? true : undefined}
                        style={{ //This must be explicitly defined here rather than in an external stylesheet for the JS resizing to work as implemented
                            width: sidebarWidthSignal.value,
                            flexBasis: sidebarWidthSignal.value,
                            minWidth: minWidthComputed.value,
                            maxWidth: '50vw' /*Do not change this to a value using a unit of measurement other than vw unless you are prepared to break JS resizing; see handleSidebarMouseMove in BusinessEntity.jsx*/
                        }}>
                        {/*
                            debugSignal.value && (
                                <span style={{ color:'red', position: 'absolute', top: '0', left: '0', pointerEvents:'none' }}>Saved sidebar width: {savedSidebarWidthSignal.value}</span>
                            )
                        */}
                        <div id="sidebar-resize-handle" className="sidebar-resize-handle" onMouseDown={handleSidebarMouseDown}>
                            <div className="ellipsis">
                                <div className="dot"></div>
                                <div className="dot"></div>
                                <div className="dot"></div>
                            </div>
                        </div>
                        <div className="sidebar-content">
                            {
                                (user.businessEntityId && beId) && (
                                    <BusinessEntityTreeRoot key={beId} rootBusinessEntitySignal={rootBusinessEntitySignal} currentBusinessEntitySignal={currentBusinessEntitySignal} currentUrlFragment={currentUrlFragmentSignal.value} rootBusinessEntityId={user.businessEntityId} permissionMasks={permissionMasks} showTreeSignal={showSidebarSignal} handleToggleTree={handleToggleTree}></BusinessEntityTreeRoot>
                                )
                            }
                        </div>
                    </aside>
                    <main className="main-content">
                        <div className="main-scroll-content" id="main">
                            <Notification />
                            <Outlet context={{ config, accessToken, user, userPermissions: userPermissionsSignal.value, userPermissionsSummary: userPermissionsSummarySignal, currentUrlFragment: currentUrlFragmentSignal.value, currentBusinessEntityId: beId, accordionData }} />
                        </div>
                    </main>
                    <InactivityModal />
                </div>
            </div>
        </>
    )
}